import { FormattedMessage } from 'react-intl';
import { matchPath, useLocation } from 'react-router';
import styled from 'styled-components';

import { NavLink, Navigate } from '@sorare/routing';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import { iconBell } from '@sorare/core/src/atoms/icons/system/iconBell.ts';
import { iconCode } from '@sorare/core/src/atoms/icons/system/iconCode.ts';
import { iconCreditCard } from '@sorare/core/src/atoms/icons/system/iconCreditCard.ts';
import { iconDollar } from '@sorare/core/src/atoms/icons/system/iconDollar.ts';
import { iconEyeOff } from '@sorare/core/src/atoms/icons/system/iconEyeOff.ts';
import { iconLock } from '@sorare/core/src/atoms/icons/system/iconLock.ts';
import { iconSettings } from '@sorare/core/src/atoms/icons/system/iconSettings.ts';
import { iconUser } from '@sorare/core/src/atoms/icons/system/iconUser.ts';
import { FullWidthContainer } from '@sorare/core/src/atoms/layout/FullWidthContainer';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { ListWithDetailLayout } from '@sorare/core/src/components/layout/ListWithDetailLayout';
import { PageHeader } from '@sorare/core/src/components/navigation/PageHeader';
import {
  SettingsPage,
  TITLES,
} from '@sorare/core/src/components/settings/pages';
import {
  SETTINGS,
  SETTINGS_ACCOUNT,
  SETTINGS_CURRENCIES,
  SETTINGS_DEVELOPER,
  SETTINGS_GLOBAL_PREFERENCES,
  SETTINGS_NOTIFICATIONS,
  SETTINGS_PAYMENT,
  SETTINGS_PRIVACY_AND_SAFETY,
  SETTINGS_SECURITY,
} from '@sorare/core/src/constants/__generated__/routes';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { useEmitClickButton } from '@sorare/core/src/hooks/useEmitClickButton';
import { useFeatureFlags } from '@sorare/core/src/hooks/useFeatureFlags';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { RequireAuth } from '@sorare/core/src/routing/RequireAuth';
import { AppLayout } from '@sorare/gameplay/src/components/navigation/AppLayout';

const StyledLink = styled(Horizontal)`
  padding: var(--double-unit);
  border-bottom: 1px solid var(--c-nd-100);
  &.active {
    background-color: var(--c-nd-50);
    color: var(--c-white);
  }
`;

const LINKS = {
  [SettingsPage.MY_ACCOUNT]: {
    to: SETTINGS_ACCOUNT,
    icon: iconUser,
    label: TITLES[SettingsPage.MY_ACCOUNT],
  },
  [SettingsPage.NOTIFICATIONS]: {
    to: SETTINGS_NOTIFICATIONS,
    icon: iconBell,
    label: TITLES[SettingsPage.NOTIFICATIONS],
  },
  [SettingsPage.SECURITY]: {
    to: SETTINGS_SECURITY,
    icon: iconLock,
    label: TITLES[SettingsPage.SECURITY],
  },
  [SettingsPage.GLOBAL_PREFERENCES]: {
    to: SETTINGS_GLOBAL_PREFERENCES,
    icon: iconSettings,
    label: TITLES[SettingsPage.GLOBAL_PREFERENCES],
  },
  [SettingsPage.CURRENCIES]: {
    to: SETTINGS_CURRENCIES,
    icon: iconDollar,
    label: TITLES[SettingsPage.CURRENCIES],
  },
  [SettingsPage.PRIVACY_AND_SAFETY]: {
    to: SETTINGS_PRIVACY_AND_SAFETY,
    icon: iconEyeOff,
    label: TITLES[SettingsPage.PRIVACY_AND_SAFETY],
  },
  [SettingsPage.PAYMENT]: {
    to: SETTINGS_PAYMENT,
    icon: iconCreditCard,
    label: TITLES[SettingsPage.PAYMENT],
  },
  [SettingsPage.DEVELOPER]: {
    to: SETTINGS_DEVELOPER,
    icon: iconCode,
    label: TITLES[SettingsPage.DEVELOPER],
  },
};

const SettingsLayout = () => {
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const location = useLocation();
  const emitClickButton = useEmitClickButton();
  const {
    flags: { useSelfServiceApi = false },
  } = useFeatureFlags();
  const links = Object.values(LINKS).filter(
    item => item.to !== SETTINGS_DEVELOPER || useSelfServiceApi
  );
  if (matchPath(SETTINGS, location.pathname) && isDesktopAndAbove) {
    return <Navigate replace to={SETTINGS_ACCOUNT} />;
  }

  return (
    <>
      <PageHeader text={navLabels.settings} />
      <RequireAuth>
        <AppLayout>
          <ListWithDetailLayout>
            <FullWidthContainer.NoPadding>
              {links.map(item => (
                <StyledLink
                  as={NavLink}
                  to={item.to}
                  key={item.to}
                  onClick={emitClickButton}
                >
                  <FontAwesomeIcon icon={item.icon} />
                  <FormattedMessage {...item.label} />
                </StyledLink>
              ))}
            </FullWidthContainer.NoPadding>
          </ListWithDetailLayout>
        </AppLayout>
      </RequireAuth>
    </>
  );
};
export default SettingsLayout;
